/* oxlint-disable max-lines-per-function */
import { type AnyObject, type Nullable } from '@Types';
import { getMobileAuthToken } from 'MobileAuthToken';
import { useErrorLogService } from 'ErrorLogging';
import { useSyncConfig } from 'FrontRoyalConfig';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import transformKeyCase from 'Utils/transformKeyCase';
import { storeProvider } from 'ReduxHelpers';
import { generateGuid } from 'guid';
import { isLocalDevHostname } from 'PedagoDomainConstants';
import { DuplicateMessageError } from '../redux/chat/chatSlice';

import { type BotClientContext } from '../types/Context.types';
import { type RealAiMessage, type OutgoingMessage, type CancellationTrigger } from '../types/ChatMessage.types';
import { isOutgoingMessage, createStructuredMessage, isRealAiMessage, isStructuredMessage } from '../utils/ChatMessage';
import {
    chatActions,
    chatSlice,
    getActiveConversationId,
    getActiveConversationMessages,
    getMessageFromMessageId,
    getStreamingAiMessage,
    type RootStateWithChatSlice,
    type ChatSliceState,
} from '../redux/chat';
import { createMockRealAiMessage } from '../SpecHelpers';
import {
    type IncomingChatSocketMessage,
    type ClientToSocketMessage,
    type ChatMessageForApi,
} from '../types/ChatWebsocket.types';
import { useWebSocket } from './useWebSocket';

// Workspace stacks resolve TutorBot on either localhost or quantic.test aliases; treat them as interchangeable.
const isWorkspaceHostname = (value: string | undefined): boolean => {
    if (!value) return false;
    const normalized = value.toLowerCase();
    return normalized === '127.0.0.1' || isLocalDevHostname(normalized);
};

// Keep the TutorBot origin aligned with the host the user actually loaded (e.g.: localhost vs quantic.test).
const resolveTutorBotOrigin = (rawOrigin: string, hostname: string): string => {
    if (!rawOrigin || !hostname) return rawOrigin;

    try {
        const url = new URL(rawOrigin);

        if (isWorkspaceHostname(url.hostname) && isWorkspaceHostname(hostname)) {
            url.hostname = hostname;
            return url.toString();
        }
    } catch {
        // no-op
    }
    return rawOrigin;
};

/**
 * A hook to submit a message/question to TutorBot over a WebSocket connection and update
 * the chat state in the Redux store as the response events stream in.
 *
 */
type StructuredMessageHandler = (payload: { type: string; [key: string]: unknown }) => void;

export function useAskTutorBot(clientContext: BotClientContext, onStructuredMessage?: StructuredMessageHandler) {
    const dispatch = useDispatch();
    const config = useSyncConfig();
    const ErrorLogService = useErrorLogService();
    const hostname = window.location.hostname;
    const origin = useMemo(() => {
        if (hostname.includes('smart.ly') || hostname.includes('smartly')) {
            return config.tutorBotSmartlyOrigin();
        }
        return resolveTutorBotOrigin(config.tutorBotOrigin(), hostname);
    }, [hostname, config]);

    const activeConversationId = useSelector(getActiveConversationId);
    const chatHistory = useSelector(getActiveConversationMessages);
    const streamingAiMessage = useSelector(getStreamingAiMessage);
    const currentMessageIdRef = useRef<string | null>(null);

    /* Parse the socket message event data (JSON string) and convert to camelCase */
    const transformSocketMessage = useCallback(
        (data: string) => transformKeyCase(JSON.parse(data), { to: 'camelCase' }) as IncomingChatSocketMessage,
        [],
    );

    /* Convert a message to snake_case before sending to the server */
    const transformClientMessage = useCallback(
        (json: AnyObject) => JSON.stringify(transformKeyCase(json, { to: 'snakeCase' }) as ClientToSocketMessage),
        [],
    );

    const handleSocketClose = useCallback(() => {
        if (!activeConversationId || !currentMessageIdRef.current) return;

        ErrorLogService.notify(`Tutorbot websocket closed unexpectedly`, null, {
            activeConversationId,
        });

        handleErrorMessage(activeConversationId, currentMessageIdRef.current);
    }, [ErrorLogService, activeConversationId]);

    /* Set up the interface for a WebSocket to the TutorBot '/chat' endpoint */
    const { open, send, close, addListener, removeListener } = useWebSocket({
        transformSocketMessage,
        transformClientMessage,
        handleSocketClose,
    });

    /* Handle incoming socket messages based on their event type */
    const handleMessage = useCallback(
        // oxlint-disable-next-line complexity
        async (socketMessage: IncomingChatSocketMessage) => {
            if (!socketMessage || !activeConversationId) return;

            /* Use the unique value tbRunId from the socket-to-client messages to ensure we update the right message locally */
            const messageId = socketMessage.meta.tbRunId;
            const eventType = socketMessage.eventType;

            currentMessageIdRef.current = messageId || null;

            switch (eventType) {
                case 'END': {
                    const status = { key: 'finished' as const };
                    dispatch(chatActions.mergeAiMessageAttrs({ messageId, attrs: { complete: true, status } }));
                    await close();
                    break;
                }
                case 'ERROR': {
                    ErrorLogService.notify(
                        `Received error from tutorbot api: ${socketMessage?.payload?.message || 'No message'}`,
                        null,
                        {
                            activeConversationId,
                        },
                    );

                    handleErrorMessage(activeConversationId, messageId);

                    await close();
                    break;
                }
                case 'START': {
                    const { version, context, algorithm } = socketMessage.meta;
                    dispatch(
                        chatActions.mergeAiMessageAttrs({
                            messageId,
                            attrs: {
                                aiMessageMetadata: {
                                    botVersion: version,
                                    algorithm,
                                } as RealAiMessage['aiMessageMetadata'],
                                conversationContext: context,
                            },
                        }),
                    );
                    break;
                }
                case 'TOKEN_FOR_DISPLAY': {
                    dispatch(
                        chatActions.appendContentToken({
                            messageId,
                            token: socketMessage.payload.token as string,
                            now: Date.now(),
                        }),
                    );
                    break;
                }
                case 'STATUS_UPDATE': {
                    const status = { key: socketMessage.payload.status } as RealAiMessage['status'];
                    dispatch(chatActions.mergeAiMessageAttrs({ messageId, attrs: { status } }));
                    break;
                }
                case 'COST': {
                    const {
                        totalTokens,
                        totalTokensFromCache,
                        promptTokens,
                        promptTokensFromCache,
                        completionTokens,
                        completionTokensFromCache,
                        totalCost,
                        totalCostFromCache,
                    } = socketMessage.payload;
                    const aiMessageMetadata = {
                        totalTokens,
                        totalCostFromCache,
                        promptTokens,
                        promptTokensFromCache,
                        completionTokens,
                        completionTokensFromCache,
                        totalCost,
                        totalTokensFromCache,
                    } as RealAiMessage['aiMessageMetadata'];
                    dispatch(chatActions.mergeAiMessageAttrs({ messageId, aiMessageMetadata }));
                    break;
                }
                case 'RESULT': {
                    const { sources, answer, footnoteReferences } = socketMessage.payload;
                    dispatch(
                        chatActions.mergeAiMessageResult({
                            messageId,
                            sources,
                            footnoteReferences,
                            answer,
                            completedAt: Date.now(),
                        }),
                    );
                    break;
                }
                case 'STRUCTURED_MESSAGE': {
                    // The `messageId` here is the skeleton AI text message that was set up to accept the
                    // streaming text back from the server. At this point, the metadata should include the
                    // bot version and algorithm, since those were set in the START event. The cost information
                    // will not be set yet. The AI text message will end up including the cost for the call to
                    // the API that generated this structured message AND the call that generated the actual
                    // text that was streamed back.
                    const structuredMessageId = generateGuid();
                    const state = storeProvider.store?.getState() as RootStateWithChatSlice | undefined;
                    const sourceMessage = state ? getMessageFromMessageId(state, messageId) : null;
                    const sourceMetadata = isRealAiMessage(sourceMessage) ? sourceMessage.aiMessageMetadata : null;
                    const conversationContext = isRealAiMessage(sourceMessage) ? sourceMessage.conversationContext : {};
                    const chatState = state
                        ? (state[chatSlice.name as keyof RootStateWithChatSlice] as ChatSliceState)
                        : null;
                    const structuredMessageAlreadyStored =
                        chatState &&
                        Object.values(chatState.messages).some(
                            message => isStructuredMessage(message) && message.sourceMessageId === messageId,
                        );
                    if (!structuredMessageAlreadyStored) {
                        const structuredMessage = createStructuredMessage({
                            conversationId: activeConversationId,
                            payload: socketMessage.payload as AnyObject,
                            id: structuredMessageId,
                            aiMessageMetadata: sourceMetadata,
                            conversationContext,
                            sourceMessageId: messageId,
                        });
                        try {
                            dispatch(chatActions.addMessage({ message: structuredMessage }));
                        } catch (error) {
                            if (!(error instanceof DuplicateMessageError)) {
                                throw error;
                            }
                        }
                    }

                    if (onStructuredMessage && 'type' in socketMessage.payload) {
                        onStructuredMessage(socketMessage.payload as { type: string; [key: string]: unknown });
                    }
                    break;
                }
                default:
                /* no-op */
            }
        },
        [close, dispatch, activeConversationId, ErrorLogService, onStructuredMessage],
    );

    const closeSocket = useCallback(async () => {
        await close();
    }, [close]);

    const hideIncompleteMessages = useCallback(() => {
        dispatch(chatActions.hideIncompleteMessages());
    }, [dispatch]);

    /* Log a WebSocket connection error to Sentry */
    const handleWsConnectionError = useCallback(
        (aiMessageId: string, err: unknown) => {
            // oxlint-disable-next-line no-console
            console.error(err);
            dispatch(chatActions.mergeAiMessageAttrs({ messageId: aiMessageId, attrs: { hasError: true } }));
            closeSocket();
            hideIncompleteMessages();
        },
        [dispatch, closeSocket, hideIncompleteMessages],
    );

    /* Open the TutorBot chat WebSocket, and send the user's message */
    const sendMessage = useCallback(
        async ({
            message: outgoingMessage,
            relevantCohortId,
        }: {
            message: OutgoingMessage;
            // relevantCohortId is null for institutional users and not required in lesson_player ctx
            relevantCohortId: Nullable<string>;
        }) => {
            if (!activeConversationId) {
                throw new Error('Cannot send a message without an activeConversationId');
            }
            if (activeConversationId !== outgoingMessage.conversationId) {
                /* FIXME: this will trigger erroneously in the following case:
                     * preview mode
                     * Chat has an initialMessage
                     * Chat has endConversationOnClose
                In that case, one of the Chat components will end the conversation when it unmounts. The other
                will not have unmounted yet, so it immediately creates a new conversation and tries to send the
                initialMessage in it again.
                I don't think this causes any user-facing issue, so I'm ignoring it for now.
                */
                throw new Error('Cannot send a message that is not part of the active conversation');
            }

            /*
                Add the client message to the local chat history.
                In preview mode in the lesson editor, 2 <Chat> components exist for the same conversation (one in desktop view and one in phone view).
                Those two components might try to send the initialMessage at the same time. We don't want to send out 2 messages,
                so we only send the message if it's not already in the conversation.
            */
            try {
                dispatch(chatActions.addMessage({ message: outgoingMessage }));
            } catch (error) {
                if (error instanceof DuplicateMessageError) {
                    return;
                }
                throw error;
            }

            /* Add the skeleton AI response message to the store, which
               we will update when the response data streams in */
            const responseMessage: RealAiMessage = createMockRealAiMessage({ conversationId: activeConversationId });
            // skip logging on the initial message action, wait for it to finish streaming
            dispatch(chatActions.addMessage({ message: responseMessage, skipLogging: true }));

            const messagesForApi: ChatMessageForApi[] = chatHistory.concat([outgoingMessage]).map(m => ({
                content: m.content,
                role: m.role,
                payload: isOutgoingMessage(m) ? m.payload : null,
            }));

            /* Open a WebSocket to TutorBot */
            open(
                _getChatSocketUrl({
                    tutorBotOrigin: origin,
                    messageId: responseMessage.id,
                    cohortId: relevantCohortId,
                    uiContext: clientContext.uiContext,
                }).toString(),
            )
                .then(() => {
                    /* Send the message to the server via the socket */
                    const messageToSocket: ClientToSocketMessage = {
                        eventType: 'ASK',
                        payload: {
                            messages: messagesForApi,
                            clientContext,
                        },
                    };
                    send(messageToSocket);
                })
                .catch(err => handleWsConnectionError(responseMessage.id, err));
        },
        [open, send, dispatch, activeConversationId, origin, handleWsConnectionError, clientContext, chatHistory],
    );

    const cancelCurrentStreamingMessage = useCallback(
        async (cancellationTrigger: CancellationTrigger) => {
            await closeSocket(); // Still close the websocket even if there is no streaming message yet
            if (streamingAiMessage) {
                dispatch(
                    chatActions.mergeAiMessageAttrs({
                        messageId: streamingAiMessage!.id,
                        attrs: { complete: true, canceled: cancellationTrigger },
                    }),
                );
            } else {
                // AI message may be loading but not yet streaming
                hideIncompleteMessages();
            }
        },
        [closeSocket, streamingAiMessage, hideIncompleteMessages, dispatch],
    );

    /* Listen for WebSocket events */
    useEffect(() => {
        addListener(handleMessage);
        return () => removeListener(handleMessage);
    }, [addListener, handleMessage, removeListener]);

    return { sendMessage, closeSocket, cancelCurrentStreamingMessage };
}

function handleErrorMessage(conversationId: string, messageId: string) {
    const { dispatch, getState } = storeProvider.store!;
    const state = getState() as RootStateWithChatSlice;

    const existingMessage = getMessageFromMessageId(state, messageId);

    // If the socket errored before a message was created and added to the store, we need to dispatch a new message
    if (!existingMessage) {
        const message = createMockRealAiMessage({ conversationId });
        dispatch(chatActions.addMessage({ message: { ...message, hasError: true, complete: true } }));
        return;
    }

    dispatch(chatActions.mergeAiMessageAttrs({ messageId, attrs: { hasError: true, complete: true } }));
}

/* Build the /chat socket endpoint URL based on the configured TutorBot origin URL */
function _getChatSocketUrl({
    tutorBotOrigin,
    messageId,
    cohortId,
    uiContext,
}: {
    tutorBotOrigin: string;
    messageId: string;
    cohortId: Nullable<string>;
    uiContext: BotClientContext['uiContext'];
}): URL {
    const url = new URL(tutorBotOrigin);
    /* determine whether to use `wss` or `ws` in this environment */
    url.protocol = ['https:', 'wss:'].includes(url.protocol) ? 'wss:' : 'ws:';
    url.pathname = '/chat';

    // In Capacitor, send the auth token as a query param to TutorBot so it can auth with BackRoyal without cookies
    if (window.CORDOVA) {
        const authToken = getMobileAuthToken();
        if (authToken) {
            url.searchParams.set('auth-token', authToken);
        }
    }

    url.searchParams.set('message-id', messageId);
    if (cohortId) url.searchParams.set('cohort-id', cohortId);
    url.searchParams.set('ui-context', uiContext);
    url.searchParams.set('api-version', '2');

    return url;
}
